import { Box, Container, Stack, useMediaQuery, useTheme } from '@mui/material';
import { FilterParams } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ArticleCardSlider } from '../../../components/card-slider/article-card-slider/article-card-slider';
import { PoolPageHeader } from '../../../components/page-header/page-header';
import { ROUTES } from '../../../utils/routes';
import { SupplerInfoAccordion } from './supplier-info-accordion';
import { SupplerInfoContactBox } from './supplier-info-contact-box';
import { articleSearchResultsSelector } from '../../../store/articles-selectors';
import { fetchArticleSearchResult } from '../../../store/articles-actions';
import { fetchSupplierDetail, supplierDetailReset } from '../../../store/supplier-actions';
import { supplierDetailSelector } from '../../../store/supplier-selectors';
import { useAppDispatch, useAppSelector } from '../../../../store/store';

export const SupplierDetail = (): ReactElement => {
    const { id: paramsId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const supplier = useAppSelector(supplierDetailSelector);
    const articles = useAppSelector(articleSearchResultsSelector);
    const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
    const [articlePage, setArticlePage] = useState(1);
    const supplierId = +(paramsId || '0');

    const searchParams = useRef(new URLSearchParams({ [FilterParams.SupplierIds]: supplierId.toString() })).current;

    useEffect(() => {
        if (supplierId) {
            dispatch(fetchSupplierDetail(supplierId));
        }
        return () => {
            dispatch(supplierDetailReset());
        };
    }, [dispatch, navigate, supplierId]);

    useEffect(() => {
        searchParams.set(FilterParams.Page, articlePage.toString());
        dispatch(fetchArticleSearchResult(searchParams));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [articlePage, dispatch, supplierId]);

    return (
        <>
            <PoolPageHeader variant="grey">{supplier?.name ?? ''}</PoolPageHeader>

            <Box sx={{ width: '100%', backgroundColor: 'grey.100' }}>
                <Container maxWidth="xl" sx={{ pt: { xs: 4, sm: 6 }, pb: 6 }}>
                    <Stack direction={isMobile ? 'column' : 'row'} gap={isMobile ? 6 : 12}>
                        <SupplerInfoAccordion />
                        <SupplerInfoContactBox />
                    </Stack>
                </Container>
            </Box>
            {!!articles.searchResults.length && (
                <Box sx={{ pb: 6, mt: { xs: 4, sm: 6 } }}>
                    <ArticleCardSlider
                        title={<FormattedMessage id="app.supplier.articles" values={{ name: supplier?.name ?? '' }} />}
                        articles={articles.searchResults}
                        totalItems={articles.total}
                        showAllUrl={{
                            pathname: ROUTES.searchPage,
                            search: new URLSearchParams({
                                [FilterParams.SupplierIds]: supplierId.toString(),
                            }).toString(),
                        }}
                        onLoadMore={() => setArticlePage(articlePage + 1)}
                    />
                </Box>
            )}
        </>
    );
};
